import React from 'react';
import { Button, Loading, Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { SPACE_ALPHANUMERIC_REGEX } from 'Utils';
import { FormikFieldString } from '../FormikComponents';
import styles from './createEditGroupModal.css';

const CreateEditGroupModal = ({
    isOpen,
    closeModal,
    mutation,
    existingGroups,
    selectedGroupName,
    deleteGroup,
    onDelete,
    isDeleteGroupOption
}: {
    isOpen: boolean;
    closeModal(): void;
    mutation?: any;
    existingGroups: string[];
    selectedGroupName?: string | null;
    deleteGroup?: EmptyRuleGroup;
    onDelete?: Function | undefined;
    isDeleteGroupOption?: boolean;
}) => {
    const { t } = useTranslation();
    const validationT = getTranslationValidationHandling(t);
    const title = selectedGroupName ? t('EditGroup') : t('CreateGroup');
    const groupName = '';

    const AddSchema = Yup.object().shape({
        groupName: Yup.string()
            .trim(validationT('noLeadingTrailingSpaces'))
            .strict()
            .min(1, validationT('minLength', { field: groupName, charCount: 1 }))
            .max(150, validationT('maxLength', { field: groupName, charCount: 150 }))
            .matches(SPACE_ALPHANUMERIC_REGEX, validationT('onlyAlphaNumeric'))
            .test({
                name: 'groupName',
                params: { groupName },
                message: t('uniqueRuleGroupNameError'),
                test: (value = '') => {
                    return !existingGroups.includes(value);
                }
            })
            .test({
                name: 'groupNameRestricted',
                params: { groupName },
                message: t('restrictedRuleGroupNameError'),
                test: (value = '') => {
                    return value.toLowerCase() !== 'overrides';
                }
            })
    });

    const initialValues = {
        groupName: selectedGroupName || ''
    };

    const handleSave = (groups: Group[]) => {
        mutation.mutate(groups);
    };
    const isLoading = mutation?.isLoading;
    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            closeOnClickOutside={!isLoading}
            containerClass={styles.modalWrapper}
        >
            <Modal.Title
                id='create-rule-group-modal-title'
                title={t(title)}
                onClose={closeModal}
                isSubmitting={isLoading}
            />
            <Formik
                initialValues={initialValues}
                validateOnMount
                validationSchema={AddSchema}
                onSubmit={(formikValues) => {
                    handleSave([{ name: formikValues.groupName }]);
                }}
            >
                {({ values, dirty, isValid, errors, handleBlur, handleChange, handleSubmit, isSubmitting }) => {
                    const disableSave = !dirty || !isValid;

                    return (
                        <div>
                            <FormikFieldString
                                label={t('Rule Group Name')}
                                name='groupName'
                                value={values.groupName}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                error={values.groupName !== selectedGroupName ? !!errors.groupName : false}
                                errorMsg={errors.groupName}
                                disabled={false}
                                optionalMsg={t('uniqueRuleGroupName')}
                                className={styles.groupNameInput}
                            />

                            <Modal.Footer
                                id='create-rule-group-modal-footer'
                                onCloseText={t('Cancel')}
                                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Save')}
                                onAction={handleSubmit}
                                onClose={closeModal}
                                disableOnCancel={isLoading}
                                disableOnAction={disableSave}
                            >
                                {isLoading && !isDeleteGroupOption && <span>{t('savingDots')}</span>}
                                {isDeleteGroupOption && deleteGroup?.id && (
                                    <Button
                                        dataTestId='delete-group-button'
                                        variant='tertiary'
                                        size='medium'
                                        onClick={() => onDelete && onDelete()}
                                    >
                                        {isSubmitting ? t('Deleting...') : t('Delete Group')}
                                    </Button>
                                )}
                            </Modal.Footer>
                        </div>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default CreateEditGroupModal;
